import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarPinState,
} from '@backstage/core-components';
import { SidebarExpandButton } from './SidebarExpandButton';
import { compatWrapper } from '@backstage/core-compat-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import { DevopsOnly } from './DevopsNavFilter';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  UserSettingsSignInAvatar,
  Settings as SidebarSettings,
} from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

/**
 * Wraps the Search SidebarGroup so that clicking the search icon
 * automatically pins (expands) the sidebar, giving the user room to type.
 */
function SearchGroupWithAutoExpand() {
  const { isPinned, toggleSidebarPinState } = useSidebarPinState();

  const handleSearchClick = () => {
    if (!isPinned) {
      toggleSidebarPinState();
    }
  };

  return (
    <div onClick={handleSearchClick} role="presentation">
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
    </div>
  );
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));
      // Skipped items
      nav.take('page:search');

      // Items restricted to devops group
      const scaffolderItem = nav.take('page:scaffolder');
      // The deploy-metrics plugin registers both a PageBlueprint (which
      // generates an implicit nav item) and an explicit NavItemBlueprint.
      // We must take both so neither leaks into nav.rest() unguarded.
      const metricsNavItem = nav.take('nav-item:deploy-metrics/deploy-metrics');
      const metricsPageItem = nav.take('page:deploy-metrics/deploy-metrics');
      const metricsItem = metricsNavItem ?? metricsPageItem;

      // Projects Registry — same pattern
      const registryNavItem = nav.take('nav-item:projects-registry/projects-registry');
      const registryPageItem = nav.take('page:projects-registry/projects-registry');
      const registryItem = registryNavItem ?? registryPageItem;

      return compatWrapper(
        <Sidebar disableExpandOnHover>
          <SidebarLogo />
          <SearchGroupWithAutoExpand />
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {nav.take('page:catalog')}
            <DevopsOnly>{scaffolderItem}</DevopsOnly>
            <SidebarDivider />
            <SidebarScrollWrapper>
              <DevopsOnly>{metricsItem}</DevopsOnly>
              <DevopsOnly>{registryItem}</DevopsOnly>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>
          <SidebarExpandButton />
        </Sidebar>,
      );
    },
  },
});
